import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faShield, faHeart, faChartLine, faSignOutAlt, faUser, faTasks, faBrain, faHandHoldingHeart, faBell, faUserFriends, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { tasksAPI, moodsAPI, burnoutAPI, recoveryAPI } from '../services/api';
import buddyService from '../services/buddyService';
import BurnoutWatchWidget from '../components/BurnoutWatchWidget';
import 'react-toastify/dist/ReactToastify.css';
import './Dashboard.css';
import NotificationBell from '../components/NotificationBell';
import PremiumUpgradeModal from '../components/buddy/PremiumUpgradeModal';
import { getUser } from '../utils/auth';


const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [moodStatus, setMoodStatus] = useState({ has_mood: false, latest_mood: null, mood_time: null });
  const [burnoutStatus, setBurnoutStatus] = useState({ status: 'healthy', message: 'Healthy' });
  const [recoveryStatus, setRecoveryStatus] = useState({ is_active: false, recovery: null });
  const [isFacilitator, setIsFacilitator] = useState(false);
  const [facilitatorStats, setFacilitatorStats] = useState(null);
  const [hasCompanyAccess, setHasCompanyAccess] = useState(false);
  const navigate = useNavigate();
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchDashboardData();
    checkFacilitatorStatus();
    checkCompanyAccess();
  }, []);

  const checkCompanyAccess = async () => {
    try {
      const { companyAPI } = await import('../services/api');
      const response = await companyAPI.checkAccess();
      setHasCompanyAccess(response.data.has_access);
    } catch (error) {
      // silently fail — user just won't see company button
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('=== Fetching Dashboard Data ===');
      
      // Fetch all dashboard data in parallel
      const [tasksResponse, moodResponse, burnoutResponse, recoveryResponse] = await Promise.all([
        tasksAPI.getTodayTasks().catch(err => {
          console.error('Tasks API error:', err);
          return { data: { tasks: [] } };
        }),
        moodsAPI.getTodayMoods().catch(err => {
          console.error('Moods API error:', err);
          return { data: { moods: [] } };
        }),
        burnoutAPI.getStatus().catch(err => {
          console.error('Burnout API error:', err);
          return { data: { status: 'healthy', message: 'Healthy' } };
        }),
        recoveryAPI.getStatus().catch(err => {
          console.error('Recovery API error:', err);
          return { data: { is_active: false, recovery: null } };
        })
      ]);

      // Log responses for debugging
      console.log('Tasks Response:', tasksResponse.data);
      console.log('Moods Response:', moodResponse.data);
      console.log('Burnout Response:', burnoutResponse.data);
      console.log('Recovery Response:', recoveryResponse.data);

      // Set tasks
      const tasksList = tasksResponse.data?.tasks || [];
      console.log('Setting tasks:', tasksList.length, 'tasks');
      setTasks(tasksList);
      
      // Process mood data - show most recent mood from today
      const moods = moodResponse.data?.moods || [];
      console.log('Processing moods:', moods.length, 'moods found');
      
      if (moods.length > 0) {
        const latestMood = moods[0];  // API returns sorted by created_at desc
        console.log('Latest mood:', latestMood);
        setMoodStatus({
          has_mood: true,
          latest_mood: latestMood.mood_type,
          mood_time: latestMood.mood_time,
          created_at: latestMood.created_at
        });
      } else {
        console.log('No moods found for today');
        setMoodStatus({
          has_mood: false,
          latest_mood: null,
          mood_time: null
        });
      }

      setBurnoutStatus(burnoutResponse.data);
      setRecoveryStatus(recoveryResponse.data);
      
      console.log('=== Dashboard Data Loaded ===');
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const checkFacilitatorStatus = async () => {
    try {
      const response = await buddyService.checkFacilitatorStatus();
      setIsFacilitator(response.is_facilitator && response.is_active);
      
      if (response.is_facilitator && response.is_active) {
        // Fetch facilitator stats
        const stats = await buddyService.getFacilitatorStats();
        setFacilitatorStats(stats);
      }
    } catch (error) {
      console.error('Error checking facilitator status:', error);
      setIsFacilitator(false);
    }
  };

  // Calculate task stats
  const activeTasks = tasks.filter(t => t.status === 'active');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  const pausedTasks = tasks.filter(t => t.status === 'paused');

  console.log('Task Stats:', {
    total: tasks.length,
    active: activeTasks.length,
    completed: completedTasks.length,
    paused: pausedTasks.length
  });

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  // Get mood display info
  const getMoodDisplay = () => {
    if (!moodStatus.has_mood) {
      return {
        text: 'Not Set',
        color: '#999',
        emoji: '😐'
      };
    }

    const moodEmojis = {
      calm: { emoji: '😌', color: '#4ade80' },
      neutral: { emoji: '😐', color: '#fbbf24' },
      tired: { emoji: '😴', color: '#60a5fa' },
      heavy: { emoji: '😔', color: '#ef4444' },
      motivated: { emoji: '💪', color: '#a78bfa' }
    };

    const moodInfo = moodEmojis[moodStatus.latest_mood] || { emoji: '😐', color: '#999' };
    
    return {
      text: moodStatus.latest_mood,
      color: moodInfo.color,
      emoji: moodInfo.emoji,
      time: moodStatus.mood_time
    };
  };

  const moodDisplay = getMoodDisplay();

  return (
    <div style={{
      minHeight: '100vh'
    }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      
      {/* Navigation Bar */}
      <nav style={{ backgroundColor: '#1a1a1a', borderBottom: '1px solid #333', position: 'relative', zIndex: 100 }}>
        {/* Top bar */}
        <div style={{ padding: '0.85rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img src="/bestie_logo.png" alt="Bestie" style={{ width: '1.5rem', height: '1.5rem' }} />
            <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#FFB6C1' }}>BestieConnect</span>
          </div>
          {/* Right side: notification + hamburger */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <NotificationBell />
            <button
              onClick={() => setMobileMenuOpen(o => !o)}
              style={{ background: 'none', border: 'none', color: '#FFB6C1', cursor: 'pointer', fontSize: '1.3rem', display: 'flex', alignItems: 'center', padding: '0.25rem' }}
              aria-label="Toggle menu"
            >
              <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
            </button>
          </div>
        </div>

        {/* Desktop nav links - hidden on mobile via inline media trick using max-width class */}
        <div className="dash-nav-desktop" style={{ display: 'flex', gap: '0.25rem', padding: '0 1rem 0.75rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Dashboard', icon: faChartLine, path: '/dashboard', color: '#FFB6C1' },
              { label: 'Tasks', icon: faTasks, path: '/tasks' },
              { label: 'Burnout Watch', icon: faShield, path: '/burnout-watch' },
              { label: 'Recovery', icon: faHandHoldingHeart, path: '/recovery' },
              { label: '💝 Donate', icon: null, path: '/donations' },
              { label: 'Reminders', icon: faBell, path: '/settings/reminders' },
            ].map(({ label, icon, path, color }) => (
              <button key={path} onClick={() => navigate(path)} style={{ background: 'none', border: 'none', color: color || '#fff', cursor: 'pointer', fontSize: '0.9rem', padding: '0.4rem 0.75rem', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                {icon && <FontAwesomeIcon icon={icon} style={{ marginRight: '0.35rem' }} />}{label}
              </button>
            ))}
            {(user.user_type === 'enterprise' || hasCompanyAccess) && (
              <button onClick={() => navigate('/company')} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '0.9rem', padding: '0.4rem 0.75rem', borderRadius: '6px', whiteSpace: 'nowrap' }}>💼 Company</button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}>
              <FontAwesomeIcon icon={faUser} /> {user.username || user.email}
            </button>
            <button onClick={handleLogout} style={{ background: 'none', border: '1px solid #FFB6C1', color: '#FFB6C1', padding: '0.4rem 0.85rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              <FontAwesomeIcon icon={faSignOutAlt} /> Logout
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="dash-nav-mobile" style={{ borderTop: '1px solid #333', padding: '0.75rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {[
              { label: 'Dashboard', icon: faChartLine, path: '/dashboard', color: '#FFB6C1' },
              { label: 'Tasks', icon: faTasks, path: '/tasks' },
              { label: 'Burnout Watch', icon: faShield, path: '/burnout-watch' },
              { label: 'Recovery', icon: faHandHoldingHeart, path: '/recovery' },
              { label: '💝 Donate', icon: null, path: '/donations' },
              { label: 'Reminders', icon: faBell, path: '/settings/reminders' },
            ].map(({ label, icon, path, color }) => (
              <button key={path} onClick={() => { navigate(path); setMobileMenuOpen(false); }} style={{ background: 'none', border: 'none', color: color || '#fff', cursor: 'pointer', fontSize: '1rem', padding: '0.65rem 0.5rem', textAlign: 'left', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                {icon && <FontAwesomeIcon icon={icon} style={{ width: '1rem' }} />}{label}
              </button>
            ))}
            {(user.user_type === 'enterprise' || hasCompanyAccess) && (
              <button onClick={() => { navigate('/company'); setMobileMenuOpen(false); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1rem', padding: '0.65rem 0.5rem', textAlign: 'left' }}>💼 Company</button>
            )}
            <div style={{ borderTop: '1px solid #333', marginTop: '0.5rem', paddingTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '1rem', padding: '0.65rem 0.5rem', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <FontAwesomeIcon icon={faUser} /> {user.username || user.email}
              </button>
              <button onClick={handleLogout} style={{ background: 'none', border: '1px solid #FFB6C1', color: '#FFB6C1', padding: '0.65rem 1rem', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem', textAlign: 'left' }}>
                <FontAwesomeIcon icon={faSignOutAlt} /> Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <div style={{ padding: 'clamp(1rem, 4vw, 2rem)', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Welcome Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#FFB6C1' }}>
            Welcome back, {user.full_name || user.username}! 👋
          </h1>
          <p style={{ color: '#999' }}>Here's your wellbeing overview</p>
        </div>

      

        {/* Burnout Watch Widget - Prominent Placement */}
        <div style={{ marginBottom: '2rem' }}>
          <BurnoutWatchWidget />
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Mood Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ 
                fontSize: '2.5rem',
                marginRight: '1rem' 
              }}>
                {moodDisplay.emoji}
              </div>
              <div>
                <div style={{ fontSize: '0.9rem', color: '#999' }}>
                  {moodStatus.has_mood ? `Today's Mood (${moodStatus.mood_time})` : "Today's Mood"}
                </div>
                <div style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 'bold', 
                  textTransform: 'capitalize',
                  color: moodDisplay.color
                }}>
                  {loading ? '...' : moodDisplay.text}
                </div>
              </div>
            </div>
            <button onClick={() => navigate('/my-bestie')} style={{
              backgroundColor: '#FFB6C1',
              border: 'none',
              color: '#000',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              width: '100%'
            }}>
              {moodStatus.has_mood ? 'Update Mood' : 'Log Mood'}
            </button>
          </div>

          {/* Tasks Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem',
            cursor: 'pointer'
          }}
          onClick={() => navigate('/tasks')}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <FontAwesomeIcon icon={faCircleCheck} style={{ fontSize: '2rem', color: '#4ade80', marginRight: '1rem' }} />
              <div>
                <div style={{ fontSize: '0.9rem', color: '#999' }}>Today's Tasks</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {loading ? '...' : `${activeTasks.length} Active`}
                </div>
              </div>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>
              {!loading && (
                <>
                  <span style={{ color: '#4ade80' }}>{completedTasks.length} completed</span>
                  {pausedTasks.length > 0 && (
                    <> • <span style={{ color: '#fbbf24' }}>{pausedTasks.length} paused</span></>
                  )}
                  {tasks.length === 0 && <span style={{ color: '#999' }}>No tasks for today</span>}
                </>
              )}
            </div>
          </div>

          {/* Burnout Risk Card - Hidden, replaced by Burnout Watch Widget above */}

          {/* Recovery Status Card */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <FontAwesomeIcon icon={faHeart} style={{ 
                fontSize: '2rem', 
                color: recoveryStatus.is_active ? '#4ade80' : '#a78bfa', 
                marginRight: '1rem' 
              }} />
              <div>
                <div style={{ fontSize: '0.9rem', color: '#999' }}>Recovery Journey</div>
                <div style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold',
                  textTransform: 'capitalize' 
                }}>
                  {loading ? '...' : 
                   recoveryStatus.is_active ? 
                   (recoveryStatus.recovery?.recovery_type?.replace('_', ' ') || 'Active') : 
                   'Not Started'}
                </div>
              </div>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>
              {!loading && recoveryStatus.is_active && recoveryStatus.recovery?.started_at && (
                <span>
                  Started: {new Date(recoveryStatus.recovery.started_at).toLocaleDateString()}
                </span>
              )}
              {!loading && !recoveryStatus.is_active && (
                <button onClick={() => navigate('/recovery')} style={{
                  background: '#FFB6C1',
                  border: 'none',
                  color: '#000',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  width: '100%'
                }}>
                  Start Journey
                </button>
              )}
            </div>
          </div>

          {/* Facilitator Dashboard Card - Only shown if user is a facilitator */}
          {isFacilitator && facilitatorStats && (
            <div style={{
              backgroundColor: '#1a1a1a',
              border: '2px solid #FFB6C1',
              borderRadius: '12px',
              padding: '1.5rem',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/facilitator/dashboard')}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <FontAwesomeIcon icon={faUserFriends} style={{ 
                  fontSize: '2rem', 
                  color: '#FFB6C1', 
                  marginRight: '1rem' 
                }} />
                <div>
                  <div style={{ fontSize: '0.9rem', color: '#999' }}>Facilitator Dashboard</div>
                  <div style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold',
                    color: '#FFB6C1'
                  }}>
                    {facilitatorStats.pending_requests} Pending
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666' }}>
                <span style={{ color: '#10b981' }}>{facilitatorStats.active_matches} active matches</span>
                <span style={{ color: '#999' }}> • {facilitatorStats.availability} slots available</span>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginBottom: '1rem', fontSize: '1.3rem' }}>Quick Actions</h2>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/tasks')} style={{
              backgroundColor: '#0a0a0a',
              border: '1px solid #FFB6C1',
              color: '#FFB6C1',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              ➕ Add Task
            </button>
            <button onClick={() => navigate('/my-bestie')} style={{
              backgroundColor: '#0a0a0a',
              border: '1px solid #4ade80',
              color: '#4ade80',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              😊 Log Mood
            </button>
            <button onClick={() => navigate('/burnout-watch')} style={{
              backgroundColor: '#0a0a0a',
              border: '1px solid #FFB6C1',
              color: '#FFB6C1',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              🛡️ Burnout Watch
            </button>
            <button onClick={() => {
              const user = getUser();
              const userType = user?.user_type || 'free';
              // Only restrict free users, allow enterprise users
              if (userType === 'free') {
                setShowPremiumModal(true);
                return;
              }
              navigate('/buddy');
            }} style={{
              backgroundColor: '#0a0a0a',
              border: '1px solid #a78bfa',
              color: '#a78bfa',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              💬 Book Buddy Session
            </button>
            {showPremiumModal && (
              <PremiumUpgradeModal onClose={() => setShowPremiumModal(false)} />
            )}
            {isFacilitator && (
              <button onClick={() => navigate('/facilitator/dashboard')} style={{
                backgroundColor: '#0a0a0a',
                border: '2px solid #FFB6C1',
                color: '#FFB6C1',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                <FontAwesomeIcon icon={faUserFriends} /> Facilitator Dashboard
                {facilitatorStats && facilitatorStats.pending_requests > 0 && (
                  <span style={{
                    marginLeft: '0.5rem',
                    backgroundColor: '#FFB6C1',
                    color: '#000',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '700'
                  }}>
                    {facilitatorStats.pending_requests}
                  </span>
                )}
              </button>
            )}
            {(user.user_type === 'enterprise' || hasCompanyAccess) && (
              <button onClick={() => navigate('/company')} style={{
                backgroundColor: '#0a0a0a',
                border: '1px solid #3b82f6',
                color: '#3b82f6',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem'
              }}>
                💼 Company Dashboard
              </button>
            )}
            <button onClick={() => navigate('/donations')} style={{
              backgroundColor: '#0a0a0a',
              border: '1px solid #ef4444',
              color: '#ef4444',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              💝 Support Us
            </button>
            <button onClick={() => navigate('/profile')} style={{
              backgroundColor: '#0a0a0a',
              border: '1px solid #fbbf24',
              color: '#fbbf24',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              ⚙️ Settings & Profile
            </button>
          </div>
        </div>

        {/* Today's Tasks Preview */}
        {!loading && tasks.length > 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.3rem', margin: 0 }}>Today's Tasks</h2>
              <button 
                onClick={() => navigate('/tasks')}
                style={{
                  background: 'none',
                  border: '1px solid #FFB6C1',
                  color: '#FFB6C1',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                View All →
              </button>
            </div>
            
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {activeTasks.slice(0, 3).map(task => (
                <div 
                  key={task.id}
                  style={{
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    padding: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>{task.title}</div>
                    {task.description && (
                      <div style={{ fontSize: '0.85rem', color: '#999' }}>
                        {task.description.length > 60 ? task.description.substring(0, 60) + '...' : task.description}
                      </div>
                    )}
                  </div>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    backgroundColor: task.weight === 'heavy' ? '#FF69B420' : task.weight === 'medium' ? '#FFB6C120' : '#99999920',
                    color: task.weight === 'heavy' ? '#FF69B4' : task.weight === 'medium' ? '#FFB6C1' : '#999'
                  }}>
                    {task.weight}
                  </div>
                </div>
              ))}
              
              {activeTasks.length > 3 && (
                <div style={{ textAlign: 'center', color: '#999', fontSize: '0.9rem', paddingTop: '0.5rem' }}>
                  +{activeTasks.length - 3} more tasks
                </div>
              )}
            </div>
          </div>
        )}

        {/* No Tasks Message */}
        {!loading && tasks.length === 0 && (
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '2rem',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
            <h3 style={{ color: '#FFB6C1', marginBottom: '0.5rem' }}>No tasks scheduled for today</h3>
            <p style={{ color: '#999', marginBottom: '1rem' }}>
              Take a break or add a new task to get started
            </p>
            <button onClick={() => navigate('/tasks')} style={{
              backgroundColor: '#FFB6C1',
              border: 'none',
              color: '#000',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}>
              Add Your First Task
            </button>
          </div>
        )}

        {/* Welcome Message */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#FFB6C1', marginBottom: '0.5rem' }}>🎉 Welcome to BestieConnect!</h3>
          <p style={{ color: '#999', marginBottom: '1rem' }}>
            Your journey to better mental wellbeing starts here. Track your mood, manage tasks, and prevent burnout.
          </p>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>
            Account type: <span style={{ color: '#FFB6C1', fontWeight: '600', textTransform: 'capitalize' }}>{user.user_type || 'Free'}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
